import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SearchForm = ({ onSearch, isLoading = false }) => {
  const [searchParams, setSearchParams] = useState({
    makes: [],
    models: [],
    yearFrom: '',
    yearTo: '',
    hoursRanges: [],
    regions: [],
    conditions: []
  });

  const craneManufacturers = [
    { value: 'liebherr', label: 'Liebherr' },
    { value: 'grove', label: 'Grove' },
    { value: 'terex', label: 'Terex' },
    { value: 'manitowoc', label: 'Manitowoc' },
    { value: 'tadano', label: 'Tadano' },
    { value: 'link-belt', label: 'Link-Belt' },
    { value: 'kobelco', label: 'Kobelco' },
    { value: 'xcmg', label: 'XCMG' },
    { value: 'sany', label: 'SANY' },
    { value: 'zoomlion', label: 'Zoomlion' }
  ];

  const craneModels = {
    liebherr: [
      { value: 'ltm-1070-4.2', label: 'LTM 1070-4.2' },
      { value: 'ltm-1090-4.2', label: 'LTM 1090-4.2' },
      { value: 'ltm-1130-5.1', label: 'LTM 1130-5.1' },
      { value: 'ltm-1160-5.2', label: 'LTM 1160-5.2' },
      { value: 'ltm-1200-5.1', label: 'LTM 1200-5.1' }
    ],
    grove: [
      { value: 'gmk-4100l-1', label: 'GMK4100L-1' },
      { value: 'gmk-5150l', label: 'GMK5150L' },
      { value: 'gmk-6300l', label: 'GMK6300L' },
      { value: 'rt-890e', label: 'RT890E' },
      { value: 'rt-9130e', label: 'RT9130E' }
    ],
    terex: [
      { value: 'ac-100-4l', label: 'AC 100/4L' },
      { value: 'ac-160-2', label: 'AC 160-2' },
      { value: 'rt-780', label: 'RT 780' },
      { value: 'rt-1120', label: 'RT 1120' },
      { value: 'crossover-8000', label: 'Crossover 8000' }
    ],
    manitowoc: [
      { value: 'mrt-2150', label: 'MRT 2150' },
      { value: 'mrt-3050', label: 'MRT 3050' },
      { value: '18000', label: '18000' },
      { value: '21000', label: '21000' }
    ],
    tadano: [
      { value: 'gr-1000xl-4', label: 'GR-1000XL-4' },
      { value: 'gr-1450ex-3', label: 'GR-1450EX-3' },
      { value: 'atf-400g-6', label: 'ATF 400G-6' },
      { value: 'atf-220g-5', label: 'ATF 220G-5' }
    ]
  };

  const yearOptions = Array.from({ length: 25 }, (_, i) => {
    const year = new Date()?.getFullYear() - i;
    return { value: year?.toString(), label: year?.toString() };
  });

  const hoursRanges = [
    { value: '0-500', label: '0 - 500 hours' },
    { value: '501-1000', label: '501 - 1,000 hours' },
    { value: '1001-2500', label: '1,001 - 2,500 hours' },
    { value: '2501-5000', label: '2,501 - 5,000 hours' },
    { value: '5001-10000', label: '5,001 - 10,000 hours' },
    { value: '10000+', label: '10,000+ hours' }
  ];

  const regions = [
    { value: 'northeast', label: 'Northeast US' },
    { value: 'southeast', label: 'Southeast US' },
    { value: 'midwest', label: 'Midwest US' },
    { value: 'southwest', label: 'Southwest US' },
    { value: 'west', label: 'West US' },
    { value: 'canada', label: 'Canada' },
    { value: 'mexico', label: 'Mexico' }
  ];

  const conditionGrades = [
    { value: '5', label: 'Excellent (5)' },
    { value: '4', label: 'Good (4)' },
    { value: '3', label: 'Average (3)' },
    { value: '2', label: 'Fair (2)' },
    { value: '1', label: 'Poor (1)' }
  ];

  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    const expandedParams = {
      ...searchParams,
      isExpandedSearch: true,
      totalFilters: Object.values(searchParams)?.filter(val => 
        Array.isArray(val) ? val?.length > 0 : val !== ''
      )?.length
    };
    onSearch(expandedParams);
  };

  const handleClearFilters = () => {
    setSearchParams({
      makes: [],
      models: [],
      yearFrom: '',
      yearTo: '',
      hoursRanges: [],
      regions: [],
      conditions: []
    });
  };

  const getAvailableModels = () => {
    if (searchParams?.makes?.length === 0) return [];
    
    const allModels = [];
    searchParams?.makes?.forEach(make => {
      if (craneModels?.[make]) {
        allModels?.push(...craneModels?.[make]);
      }
    });
    return allModels;
  };

  const availableModels = getAvailableModels();

  const getSelectedFiltersCount = () => {
    const counts = {
      makes: searchParams?.makes?.length || 0,
      models: searchParams?.models?.length || 0,
      years: (searchParams?.yearFrom || searchParams?.yearTo) ? 1 : 0,
      hours: searchParams?.hoursRanges?.length || 0,
      regions: searchParams?.regions?.length || 0,
      conditions: searchParams?.conditions?.length || 0
    };
    return Object.values(counts)?.reduce((total, count) => total + count, 0);
  };

  const selectedFiltersCount = getSelectedFiltersCount();

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Search" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Crane Search</h2>
            <p className="text-sm text-text-secondary">
              Find and value cranes by specifications 
              {selectedFiltersCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {selectedFiltersCount} filter{selectedFiltersCount !== 1 ? 's' : ''} active
                </span>
              )}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          iconName="RotateCcw"
          iconPosition="left"
          disabled={selectedFiltersCount === 0}
        >
          Clear Filters
        </Button>
      </div>
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">Expanded Search Mode</h4>
            <p className="text-sm text-blue-700">
              Select multiple options in each category to broaden your search results. 
              The more selections you make, the more comprehensive your results will be.
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Select
          label="Manufacturers"
          placeholder="Select manufacturer(s)"
          options={craneManufacturers}
          value={searchParams?.makes}
          onChange={(value) => handleInputChange('makes', value)}
          multiple
          searchable={true}
          clearable={true}
          description={`${searchParams?.makes?.length} selected`}
        />

        <Select
          label="Models"
          placeholder="Select model(s)"
          options={availableModels}
          value={searchParams?.models}
          onChange={(value) => handleInputChange('models', value)}
          disabled={searchParams?.makes?.length === 0}
          multiple
          searchable={true}
          clearable={true}
          description={`${searchParams?.models?.length} selected`}
        />

        <Select
          label="Year From"
          placeholder="Start year"
          options={yearOptions}
          value={searchParams?.yearFrom}
          onChange={(value) => handleInputChange('yearFrom', value)}
          searchable={true}
          clearable={true}
        />

        <Select
          label="Year To"
          placeholder="End year"
          options={yearOptions}
          value={searchParams?.yearTo}
          onChange={(value) => handleInputChange('yearTo', value)}
          searchable={true}
          clearable={true}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Select
          label="Hours Ranges"
          placeholder="Select hour range(s)"
          options={hoursRanges}
          value={searchParams?.hoursRanges}
          onChange={(value) => handleInputChange('hoursRanges', value)}
          multiple
          clearable={true}
          description={`${searchParams?.hoursRanges?.length} selected`}
        />

        <Select
          label="Regions"
          placeholder="Select region(s)"
          options={regions}
          value={searchParams?.regions}
          onChange={(value) => handleInputChange('regions', value)}
          multiple
          clearable={true}
          description={`${searchParams?.regions?.length} selected`}
        />

        <Select
          label="Condition Grades"
          placeholder="Select condition(s)"
          options={conditionGrades}
          value={searchParams?.conditions}
          onChange={(value) => handleInputChange('conditions', value)}
          multiple
          clearable={true}
          description={`${searchParams?.conditions?.length} selected`}
        />
      </div>
      {selectedFiltersCount > 0 && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Active Filters Summary:</h4>
          <div className="flex flex-wrap gap-2 text-sm">
            {searchParams?.makes?.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                {searchParams?.makes?.length} Manufacturer{searchParams?.makes?.length !== 1 ? 's' : ''}
              </span>
            )}
            {searchParams?.models?.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800">
                {searchParams?.models?.length} Model{searchParams?.models?.length !== 1 ? 's' : ''}
              </span>
            )}
            {(searchParams?.yearFrom || searchParams?.yearTo) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-800">
                Year Range: {searchParams?.yearFrom || 'Any'} - {searchParams?.yearTo || 'Any'}
              </span>
            )}
            {searchParams?.hoursRanges?.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                {searchParams?.hoursRanges?.length} Hour Range{searchParams?.hoursRanges?.length !== 1 ? 's' : ''}
              </span>
            )}
            {searchParams?.regions?.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-800">
                {searchParams?.regions?.length} Region{searchParams?.regions?.length !== 1 ? 's' : ''}
              </span>
            )}
            {searchParams?.conditions?.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-800">
                {searchParams?.conditions?.length} Condition{searchParams?.conditions?.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="Target" size={16} />
          <span>
            {selectedFiltersCount === 0 
              ? 'Select multiple options to expand search results'
              : `Searching with ${selectedFiltersCount} active filter${selectedFiltersCount !== 1 ? 's' : ''} for broader results`
            }
          </span>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/comparison-tool'}
            iconName="GitCompare"
            iconPosition="left"
          >
            Compare Tool
          </Button>
          
          <Button
            variant="default"
            onClick={handleSearch}
            loading={isLoading}
            iconName="Search"
            iconPosition="left"
            className="min-w-32"
          >
            Search Cranes
            {selectedFiltersCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                {selectedFiltersCount}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchForm;